import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const NameCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.firstName} {row.original.surname}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.phone || row.original.email || "No contact info"}
    </Typography>
  </Box>
);
NameCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  follow_up_needed: "Follow-up Needed",
  converted: "Converted",
  inactive: "Inactive",
};

const StatusCell = ({ value }) => <StatusChip value={STATUS_LABELS[value] || value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Box display="flex" justifyContent="center" gap={1}>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Box>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

const emptyForm = () => ({
  id: null,
  first_name: "",
  surname: "",
  phone: "",
  email: "",
  address: "",
  visit_date: new Date().toISOString().split("T")[0],
  how_heard: "",
  follow_up_date: "",
  notes: "",
  status: "new",
});

export default function useVisitorData() {
  const { church } = useAuth();

  const [visitorData, setVisitorData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Visitor", accessor: "firstName", align: "left", width: "25%", Cell: NameCell },
      {
        Header: "Visit Date",
        accessor: "visitDate",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "How heard",
        accessor: "howHeard",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "Follow-up",
        accessor: "followUpDate",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "15%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [isSaving, setIsSaving] = useState(false);

  const fetchVisitors = useCallback(async () => {
    if (!church?.id) return;
    setVisitorData((prev) => ({ ...prev, isLoading: true }));
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/visitors`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const visitors = result.data || [];

      setVisitorData((prev) => ({
        ...prev,
        rows: visitors.map((v, index) => ({
          id: v.id,
          serial: index + 1,
          firstName: v.first_name,
          surname: v.surname,
          phone: v.phone,
          email: v.email,
          visitDate: v.visit_date ? new Date(v.visit_date).toLocaleDateString() : "",
          howHeard: v.how_heard || "—",
          followUpDate: v.follow_up_date ? new Date(v.follow_up_date).toLocaleDateString() : "—",
          status: v.status,
          originalData: v,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch visitors failed:", error);
      setVisitorData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [church?.id]);

  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  const handleAdd = () => {
    setForm(emptyForm());
    setEditModalOpen(true);
  };

  const handleEdit = useCallback((visitor) => {
    const raw = visitor.originalData || {};
    setForm({
      id: visitor.id,
      first_name: raw.first_name || "",
      surname: raw.surname || "",
      phone: raw.phone || "",
      email: raw.email || "",
      address: raw.address || "",
      visit_date: raw.visit_date ? raw.visit_date.split("T")[0] : "",
      how_heard: raw.how_heard || "",
      follow_up_date: raw.follow_up_date ? raw.follow_up_date.split("T")[0] : "",
      notes: raw.notes || "",
      status: raw.status || "new",
    });
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((visitor) => {
    setSelectedVisitor(visitor);
    setDeleteModalOpen(true);
  }, []);

  const handleSubmit = async () => {
    try {
      if (!form.first_name.trim() || !form.surname.trim()) {
        throw new Error("First name and surname are required.");
      }
      if (!form.visit_date) {
        throw new Error("Pick a visit date.");
      }

      setIsSaving(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = form.id ? `${API_BASE}/visitors/${form.id}` : `${API_BASE}/visitors`;
      const method = form.id ? "PUT" : "POST";

      const payload = { ...form };
      delete payload.id;
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "") payload[key] = null;
      });

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Visitor ${form.id ? "updated" : "added"}`,
        icon: "success",
      });
      fetchVisitors();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Save visitor failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/visitors/${selectedVisitor.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({ title: "Success!", text: "Visitor deleted", icon: "success" });
      fetchVisitors();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete visitor failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handlePrint = () => window.print();

  return {
    ...visitorData,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedVisitor,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
  };
}
